/**
 * Main-light shadow map maths (pure; the GL side lives in engine.ts and the shaders).
 *
 * Nanome's URP asset (Assets/Universal Render Pipeline Asset.asset:45-69) gives one directional light with a
 * 2048 shadow map, one cascade, shadow distance 1 m, depth bias 1.35 and normal bias 1.25 (in shadow texels), soft
 * shadows at quality 1 (Low: 4 bilinear-compare taps at +-0.5 texel), cascade border 0.3 used as the distance fade.
 * The light is fixed in view space (Environment.prefab:252-258), so the light basis never changes; only the ortho
 * box moves with the scene.
 *
 * Frustum: URP fits the shadow map to a sphere around the whole camera slice [near, shadow distance]. Lite fits a
 * tighter ortho box: the light-space xy of the visible scene bounds intersected with the camera slice up to the
 * shadow distance (1 m in world units, which is 50 Å at the initial 0.02 m/Å), and the light-space z of the whole
 * scene so every caster in front of a receiver lands in the map. Everything here is in GL view space (camera at the
 * origin looking down -z), which is also the engine's world space.
 */
import { Matrix4, Vector3 } from 'three';
import { CAMERA, LIGHTING } from '@nanome-lite/look';

export const SHADOW_MAP_SIZE = LIGHTING.shadows.mapSize;
/** URP shadow distance in metres (world units). At the initial scale (0.02 m/Å) that is 50 Å. */
export const SHADOW_DISTANCE_METRES = LIGHTING.shadows.distanceMetres;
/** Universal Render Pipeline Asset.asset:63-64 (texel units) and the soft-shadow kernel factor in URP ShadowUtils.GetShadowBias. */
export const SHADOW_DEPTH_BIAS_TEXELS = 1.35;
export const SHADOW_NORMAL_BIAS_TEXELS = 1.25;
export const SOFT_SHADOW_KERNEL_RADIUS = 2.5;
/** m_CascadeBorder 0.3: shadows fade out over the last 30 % of the shadow distance. */
export const SHADOW_FADE_BORDER = 0.3;
/** Environment.prefab m_Shadows.m_Strength. */
export const SHADOW_STRENGTH = 1;

export interface ShadowFrustum {
  /** view space -> light space (rotation only; the light looks down its -z) */
  view: Matrix4;
  /** inverse of `view` (the light camera's world matrix) */
  viewInverse: Matrix4;
  /** light space -> clip (orthographic) */
  projection: Matrix4;
  /** view space -> shadow-map texture coordinates and depth in [0, 1] */
  matrix: Matrix4;
  /** ortho box extents in metres */
  width: number;
  height: number;
  depth: number;
  /** metres per shadow texel along the wider axis (URP uses 2 / m00, i.e. the box width) */
  texelSize: number;
}

/**
 * Light basis for a view-space direction pointing TOWARD the light: light +z = toLight (so the light camera looks
 * down -z like any GL camera), x = up x z, y = z x x. Returns view -> light.
 */
export function lightViewMatrix(toLight: Vector3, out = new Matrix4()): Matrix4 {
  const z = toLight.clone().normalize();
  const up = Math.abs(z.y) > 0.99 ? new Vector3(1, 0, 0) : new Vector3(0, 1, 0);
  const x = new Vector3().crossVectors(up, z).normalize();
  const y = new Vector3().crossVectors(z, x);
  return out.set(
    x.x, x.y, x.z, 0,
    y.x, y.y, y.z, 0,
    z.x, z.y, z.z, 0,
    0, 0, 0, 1,
  );
}

/** The 8 view-space corners of the camera frustum slice [near, far] (FOV 60, the given aspect). */
export function cameraSliceCorners(aspect: number, near: number, far: number, fovDeg: number = CAMERA.fovDeg): Vector3[] {
  const t = Math.tan((fovDeg * Math.PI) / 360);
  const out: Vector3[] = [];
  for (const d of [near, far]) {
    const h = d * t, w = h * aspect;
    out.push(new Vector3(-w, -h, -d), new Vector3(w, -h, -d), new Vector3(w, h, -d), new Vector3(-w, h, -d));
  }
  return out;
}

/** 8 corners of an Å box (domain frame) padded by `pad` Å, mapped to view space by the scene root matrix. */
export function boxCornersToView(min: readonly number[], max: readonly number[], pad: number, root: Matrix4): Vector3[] {
  const out: Vector3[] = [];
  for (let i = 0; i < 8; i++) {
    out.push(new Vector3(
      (i & 1 ? max[0]! + pad : min[0]! - pad),
      (i & 2 ? max[1]! + pad : min[1]! - pad),
      (i & 4 ? max[2]! + pad : min[2]! - pad),
    ).applyMatrix4(root));
  }
  return out;
}

interface Box3 { min: Vector3; max: Vector3 }
function boundsIn(points: readonly Vector3[], m: Matrix4): Box3 {
  const min = new Vector3(Infinity, Infinity, Infinity), max = new Vector3(-Infinity, -Infinity, -Infinity);
  const p = new Vector3();
  for (const q of points) { p.copy(q).applyMatrix4(m); min.min(p); max.max(p); }
  return { min, max };
}

/** z-to-[0,1] and xy-to-[0,1] remap applied after the ortho projection. */
const TEXTURE_BIAS = new Matrix4().set(
  0.5, 0, 0, 0.5,
  0, 0.5, 0, 0.5,
  0, 0, 0.5, 0.5,
  0, 0, 0, 1,
);

/**
 * Ortho light frustum for the given scene (view-space corners of the caster bounds) and camera slice. Returns null
 * when no part of the scene is inside the slice (nothing can receive a shadow).
 *  - xy: scene box intersected with the slice box in light space, padded by two texels for the PCF taps;
 *  - z: the whole scene box (all casters), padded by 1 % so the far-side impostor proxies stay inside.
 */
export function fitShadowFrustum(sceneCorners: readonly Vector3[], sliceCorners: readonly Vector3[], toLight: Vector3, mapSize: number = SHADOW_MAP_SIZE): ShadowFrustum | null {
  if (sceneCorners.length === 0) return null;
  const view = lightViewMatrix(toLight);
  const scene = boundsIn(sceneCorners, view);
  const slice = boundsIn(sliceCorners, view);
  let minX = Math.max(scene.min.x, slice.min.x), maxX = Math.min(scene.max.x, slice.max.x);
  let minY = Math.max(scene.min.y, slice.min.y), maxY = Math.min(scene.max.y, slice.max.y);
  if (!(maxX > minX && maxY > minY)) return null;
  // Casters may sit anywhere along the light ray, but the scene must overlap the slice in light depth as well,
  // otherwise no receiver is visible.
  if (scene.max.z < slice.min.z || scene.min.z > slice.max.z) return null;
  const padXY = 2 * Math.max(maxX - minX, maxY - minY) / mapSize;
  minX -= padXY; maxX += padXY; minY -= padXY; maxY += padXY;
  const depth = scene.max.z - scene.min.z;
  const padZ = Math.max(depth * 0.01, 1e-4);
  // light camera looks down -z: near plane at z = scene.max.z, far plane at z = scene.min.z
  const near = -(scene.max.z + padZ), far = -(scene.min.z - padZ);
  const projection = new Matrix4().makeOrthographic(minX, maxX, maxY, minY, near, far);
  const matrix = new Matrix4().multiplyMatrices(TEXTURE_BIAS, projection).multiply(view);
  const width = maxX - minX, height = maxY - minY;
  return { view, viewInverse: view.clone().invert(), projection, matrix, width, height, depth: far - near, texelSize: Math.max(width, height) / mapSize };
}

/**
 * Caster bias in metres (URP ShadowUtils.GetShadowBias): depth and normal bias in texels times the texel size, times
 * the soft-shadow kernel radius 2.5. Applied in the mesh shadow vertex shader as URP ApplyShadowBias does: push away
 * from the light by `depth`, inward along the normal by `normal * (1 - saturate(N.L))`. Impostors use none: their
 * depth comes from the far-side proxy (IMPOSTOR_SHADOW_RASTER_DEPTH).
 */
export function shadowCasterBias(texelSize: number): { depth: number; normal: number } {
  return {
    depth: SHADOW_DEPTH_BIAS_TEXELS * texelSize * SOFT_SHADOW_KERNEL_RADIUS,
    normal: SHADOW_NORMAL_BIAS_TEXELS * texelSize * SOFT_SHADOW_KERNEL_RADIUS,
  };
}

/**
 * Distance fade as one MAD on the squared view distance (URP GetShadowFadeParams): 0 inside (1 - border) * distance,
 * 1 at the shadow distance. Returns [scale, offset] for fade = saturate(d2 * scale + offset).
 */
export function shadowFadeParams(distance: number = SHADOW_DISTANCE_METRES, border: number = SHADOW_FADE_BORDER): [number, number] {
  const end = distance * distance;
  const startD = distance * (1 - border);
  const start = startD * startD;
  const scale = 1 / Math.max(end - start, 1e-8);
  return [scale, -start * scale];
}
